import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/heroSlides';

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const total = heroSlides.length;

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 5200);
    return () => window.clearInterval(timer);
  }, [total]);

  const go = (next: number) => setIndex((next + total) % total);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative h-[460px] w-full overflow-hidden md:h-[560px]"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0'}`}
          aria-hidden={i !== index}
        >
          <img src={slide.image} alt={slide.title} className="h-full w-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/40 via-foreground-950/45 to-foreground-950/60" />
        </div>
      ))}

      <div className="relative z-10 flex h-full w-full flex-col items-center justify-center px-6 text-center">
        <span className="mw-fade-up rounded-full bg-accent-500 px-4 py-1 text-xs font-label font-700 tracking-wide text-foreground-950">
          {heroSlides[index].tag}
        </span>
        <h1
          key={`title-${index}`}
          className="mw-fade-up mt-4 max-w-3xl font-heading text-3xl font-800 leading-tight text-background-50 md:text-5xl"
        >
          {heroSlides[index].title}
        </h1>
        <p className="mw-fade-up mt-4 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">
          {heroSlides[index].desc}
        </p>
        <div className="mw-fade-up mt-7 flex flex-wrap items-center justify-center gap-3">
          <Link
            to={heroSlides[index].to}
            className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-label font-500 text-background-50 transition-colors hover:bg-primary-600"
          >
            查看详情
          </Link>
          <Link
            to="/category/comics"
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-50/60 bg-background-50/10 px-6 py-3 text-sm font-label font-500 text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
          >
            浏览全部漫画
          </Link>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/25 text-lg text-background-50 backdrop-blur transition-colors hover:bg-background-50/40 md:left-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-left-s-line" />
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/25 text-lg text-background-50 backdrop-blur transition-colors hover:bg-background-50/40 md:right-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-right-s-line" />
      </button>

      <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
        {heroSlides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-2 cursor-pointer rounded-full transition-all ${
              i === index ? 'w-7 bg-accent-500' : 'w-2 bg-background-50/60'
            }`}
          />
        ))}
      </div>
    </section>
  );
}